// AEGIS Support console — ticket list + thread on the existing
// /api/ops/support endpoints. THE WALL is rendered visually: the back-stage
// OPERATOR thread sits in its own clearly-walled pane (ops view only) and the
// "Client preview" toggle re-fetches the EXACT client-safe projection
// (client_safe_only=true) — operator thread filtered out server-side.
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LifeBuoy, Send, ShieldAlert, CheckCircle2, Eye, EyeOff } from "lucide-react";

import MockBadge from "@/components/MockBadge";
import StatusPill from "@/components/StatusPill";
import { support } from "@/lib/api";
import { fmtTime, shortId } from "@/lib/format";

export default function Support() {
  const [selected, setSelected] = useState(null);
  const { data: tickets = [] } = useQuery({
    queryKey: ["support-tickets"],
    queryFn: () => support.tickets({ limit: 100 }),
    refetchInterval: 8000,
    retry: false,
  });

  return (
    <div data-testid="page-support" className="p-6 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div className="min-w-0">
          <div className="pj-label mb-2">· AEGIS · 24/7 Support Console</div>
          <h1 className="font-display text-[32px] md:text-[40px] leading-[1.02]">Support</h1>
        </div>
        <div className="shrink-0"><MockBadge /></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="pj-card overflow-x-auto">
          <div className="flex items-center justify-between p-4" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
            <h2 className="font-display text-[18px] flex items-center gap-2">
              <LifeBuoy size={14} className="text-[rgb(var(--dim))]" /> Tickets
            </h2>
            <span className="mono text-[10px] uppercase tracking-[0.14em] text-[rgb(var(--dim))]">{tickets.length}</span>
          </div>
          {tickets.length === 0 && (
            <div className="p-6 mono text-[11px] text-[rgb(var(--dim))]">No tickets.</div>
          )}
          <div className="divide-y divide-[rgb(var(--ln))]">
            {tickets.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`ticket-${t.id}`}
                onClick={() => setSelected(t.id)}
                className={`w-full text-left p-3 hover:bg-[rgb(236,229,216)] ${selected === t.id ? "bg-[rgb(236,229,216)]" : ""}`}
              >
                <div className="mono text-[12px] truncate">{t.subject || shortId(t.id)}</div>
                <div className="mt-1 flex items-center gap-2 mono text-[10px] text-[rgb(var(--dim))]">
                  <StatusPill state={t.status} />
                  <span className="uppercase tracking-[0.12em]">{t.category}</span>
                  <span>· {fmtTime(t.created_at)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          {!selected && (
            <div className="pj-card p-10 mono text-[12px] text-[rgb(var(--dim))] text-center">
              Select a ticket to open the thread.
            </div>
          )}
          {selected && <TicketThread ticketId={selected} />}
        </div>
      </div>
    </div>
  );
}

function TicketThread({ ticketId }) {
  const qc = useQueryClient();
  const [clientPreview, setClientPreview] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ["support-ticket", ticketId, clientPreview],
    queryFn: () => support.ticket(ticketId, clientPreview),
    refetchInterval: 8000,
    retry: false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["support-ticket", ticketId] });
    qc.invalidateQueries({ queryKey: ["support-tickets"] });
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await support.reply(ticketId, reply.trim());
      toast.success("Reply sent (WALL-scanned)");
      setReply("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send reply");
    } finally {
      setBusy(false);
    }
  };

  const escalate = async () => {
    try {
      await support.escalate(ticketId);
      toast.success("Escalated to the human queue");
      refresh();
    } catch {
      toast.error("Failed to escalate");
    }
  };

  const resolve = async () => {
    try {
      await support.resolve(ticketId, { close: true, ask_csat: true });
      toast.success("Resolved + closed (CSAT asked)");
      refresh();
    } catch {
      toast.error("Failed to resolve");
    }
  };

  const ticket = data?.ticket;
  const messages = data?.messages || [];
  const operatorThread = data?.operator_thread || [];

  return (
    <div className="pj-card p-5" data-testid={`ticket-thread-${ticketId}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="pj-label mb-1">Ticket · {shortId(ticketId)}</div>
          <h2 className="font-display text-[20px] truncate">{ticket?.subject || "—"}</h2>
          <div className="mono text-[11px] text-[rgb(var(--dim))] mt-1 flex items-center gap-2">
            {ticket && <StatusPill state={ticket.status} />}
            <span>· {ticket?.category}</span>
            {ticket?.sla_due_at && <span>· SLA {fmtTime(ticket.sla_due_at)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            data-testid="btn-client-preview"
            onClick={() => setClientPreview((v) => !v)}
            className="mono inline-flex items-center gap-1 px-2 py-1.5 text-[10px] uppercase tracking-[0.12em] border border-[rgb(var(--border-strong))] hover:border-[rgb(169,130,28)]"
          >
            {clientPreview ? <EyeOff size={11} /> : <Eye size={11} />}
            {clientPreview ? "Ops view" : "Client preview"}
          </button>
          <button
            type="button"
            data-testid="btn-escalate"
            onClick={escalate}
            className="mono inline-flex items-center gap-1 px-2 py-1.5 text-[10px] uppercase tracking-[0.12em] border border-[rgb(var(--border-strong))] hover:border-[rgb(196,52,44)] hover:text-[rgb(196,52,44)]"
          >
            <ShieldAlert size={11} /> Escalate
          </button>
          <button
            type="button"
            data-testid="btn-resolve"
            onClick={resolve}
            className="mono inline-flex items-center gap-1 px-2 py-1.5 text-[10px] uppercase tracking-[0.12em]"
            style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
          >
            <CheckCircle2 size={11} /> Resolve
          </button>
        </div>
      </div>

      {clientPreview && (
        <div className="mono text-[10px] uppercase tracking-[0.14em] mb-3 px-3 py-2" style={{ background: "rgb(236,229,216)", color: "rgb(var(--dim))" }} data-testid="client-preview-banner">
          Client preview — EXACTLY what the client sees (operator thread removed, WALL-clean)
        </div>
      )}

      {/* Client-facing thread */}
      <div className="space-y-2" data-testid="client-thread">
        {messages.length === 0 && (
          <div className="mono text-[11px] text-[rgb(var(--dim))]">No messages.</div>
        )}
        {messages.map((m) => (
          <div key={m.id} className="pj-card p-3" style={{ background: m.direction === "in" ? "rgb(var(--surface-2))" : "rgb(var(--surface))" }}>
            <div className="mono text-[10px] uppercase tracking-[0.14em] text-[rgb(var(--dim))] mb-1">
              {m.author} · {m.direction} · {fmtTime(m.at)}
            </div>
            <div className="mono text-[12px] whitespace-pre-wrap break-words">{m.body}</div>
          </div>
        ))}
      </div>

      {/* WALLED back-stage operator thread — ops view only, never in preview */}
      {!clientPreview && operatorThread.length > 0 && (
        <div className="mt-5" data-testid="operator-thread">
          <div
            className="mono text-[10px] uppercase tracking-[0.16em] px-3 py-2 flex items-center gap-2"
            style={{ background: "rgb(38,31,24)", color: "rgb(224,196,120)" }}
          >
            <ShieldAlert size={11} /> Back-stage operator thread · THE WALL — never client-visible
          </div>
          <div className="space-y-2 p-3" style={{ border: "1px dashed rgb(var(--border-strong))", borderTop: "none" }}>
            {operatorThread.map((m) => (
              <div key={m.id} className="p-2" style={{ background: "rgb(236,229,216)" }}>
                <div className="mono text-[10px] uppercase tracking-[0.14em] text-[rgb(var(--dim))] mb-1">
                  {m.author} · {m.direction} · {fmtTime(m.at)}
                </div>
                <div className="mono text-[12px] whitespace-pre-wrap break-words">{m.body}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Human reply — routed through aegis.send_client (WALL-scanned) */}
      {!clientPreview && (
        <form onSubmit={sendReply} className="mt-5 flex gap-2" data-testid="reply-form">
          <input
            type="text"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Reply to the client (WALL-scanned before it leaves)…"
            data-testid="reply-input"
            className="flex-1 mono text-[12px] px-2 py-2"
            style={{ background: "rgb(var(--surface))", border: "1px solid rgb(var(--ln))", color: "rgb(var(--fg))" }}
          />
          <button
            type="submit"
            disabled={busy || !reply.trim()}
            data-testid="reply-submit"
            className="mono inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-[0.12em] disabled:opacity-40"
            style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
          >
            <Send size={11} /> {busy ? "Sending…" : "Send"}
          </button>
        </form>
      )}
    </div>
  );
}
